/** @format */
import React, { useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, Divider, Button, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/styles";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  formatDate,
  bootcampContentToInfoMapper,
  saveOnLS,
} from "../../helpers/courses";
import useTranslation from "../../hooks/useTranslation";
import { ColorContext } from "../../pages/Bootcamp";
import { coursesTags } from "../../context/tags";

const useStyles = (
  theme,
  isMobile,
  { mainColor, secondaryColor, iconColor }
) => ({
  inputs: {
    width: "100%",
    height: "fit-content",
    padding: "0",
    margin: "26px 0px 70px 0px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    select: {
      minWidth: "276px",
      height: "47px",
      borderRadius: "56px",
      boxShadow: "0 1px 12px 0 rgba(0, 0, 0, 0.1)",
      color: theme.palette.blue.main,
    },
  },
  sliderContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflowY: "visible",
    marginTop: "30px",
  },
  slider: {
    marginTop: isMobile ? "0" : "110px",
    width: "98%",
    height: "fit-content",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
  },
  apply: {
    width: isMobile ? "293px" : { xl: "1373px", lg: "1098px", md: "878.4px" },
    height: isMobile
      ? "fit-content"
      : { xl: "402px", lg: "402px", md: "402px" },
    borderRadius: "47px",
    background:
      "linear-gradient(to bottom, #071a3e, #071a3e, #071a3e, #071a3e, #071a3e)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflow: "hidden",
    marginTop: isMobile ? "0" : "76px",
    listItem: {
      width: "100%",
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "flex-start",
      margin: isMobile ? "0px 0px 21px 56.2px" : "0px 0px 21px 28.8px",
    },
    listText: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "flex-start",
    },
    listIcon: {
      width: "21px",
      height: "21px",
      marginTop: "5px",
    },
    applyButton: {
      enabled: {
        width: isMobile
          ? "192px"
          : { xl: "349px", lg: "279px", sm: "223.36px" },
        height: "52px",
        borderRadius: "100px",
        background: `linear-gradient(100deg, ${mainColor} 0%, ${secondaryColor} 100%)`,
        color: theme.palette.white.main,
        textTransform: "none",
        fontSize: 15.5,
        fontWeight: 550,
        margin: isMobile ? "0 0 39px" : "20px 0 0",
      },
      disabled: {
        width: isMobile
          ? "192px"
          : { xl: "349px", lg: "279px", sm: "223.36px" },
        height: "52px",
        borderRadius: "100px",
        background: `linear-gradient(100deg, ${mainColor} 0%, ${secondaryColor} 100%)`,
        textTransform: "none",
        fontSize: 15.5,
        fontWeight: 550,
        margin: isMobile ? "0 0 39px" : "53px 0 0",
      },
    },
    icon: {
      enabled: {
        p: "5px",
        height: "14px",
        width: "14px",
        borderRadius: "58px",
        color: iconColor,
        bgcolor: theme.palette.white.main,
        ml: "3px",
        boxShadow:
          "4px 8px 20px 0 rgba(0, 0, 0, 0.15), inset 7px 0 20px 0 rgba(255, 255, 255, 0.15)",
      },
      disabled: {
        p: "5px",
        height: "14px",
        width: "14px",
        borderRadius: "58px",
        color: iconColor,
        bgcolor: "#d9f1ff",
        ml: "3px",
        boxShadow:
          "4px 8px 20px 0 rgba(0, 0, 0, 0.15), inset 7px 0 20px 0 rgba(255, 255, 255, 0.15)",
      },
    },
  },
  applySecond: {
    width: "293px",
    height: "fit-content",
    borderRadius: "47px",
    border: "solid 5px #fff",
    backgroundImage: "linear-gradient(to bottom, #fcfdfe 0%, #fcfdff 100%)",
    marginTop: "82px",
    background: "linear-gradient(to bottom, #fcfdfe 0%, #fcfdff 100%)",
    boxShadow: "0 4px 92px 0 rgba(0, 31, 85, 0.06)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflow: "hidden",
  },
  typography: {
    descriptionSubtitle: {
      fontSize: isMobile ? 14 : 16,
      fontWeight: "bold",
      letterSpacing: "2px",
      color: theme.palette.red.main,
      marginBottom: "0.6rem",
    },
    descriptionTitle: {
      fontSize: isMobile ? 32 : 65,
      fontWeight: "lighter",
      fontFamily: theme.typography.montserrat.regular,
      color: theme.palette.blue.main,
      lineHeight: "43px",
    },
    title: {
      color: theme.palette.yellow.main,
      fontFamily: theme.typography.montserrat.regular,
      fontSize: 30,
      fontWeight: 900,
    },
    text: {
      fontFamily: theme.typography.anaHeim.regular,
      fontSize: 24,
      margin: "15px 15px",
    },
    apply: {
      start: {
        color: theme.palette.white.main,
        fontFamily: theme.typography.montserrat.regular,
        fontSize: isMobile ? "15px" : { xl: "28px", lg: "28px", md: "20px" },
        margin: isMobile ? "43px 0px 20px" : "0 0 0 82px",
      },
      end: {
        color: theme.palette.white.main,
        fontFamily: theme.typography.montserrat.regular,
        fontSize: isMobile ? "18px" : "24px",
        lineHeight: "26px",
        margin: isMobile ? "0" : "0 82px 0",
      },
      description: {
        color: theme.palette.white.main,
        fontFamily: theme.typography.montserrat.regular,
        fontSize: "18px",
        lineHeight: "24px",
        letterSpacing: "-0.32px",
      },
      value: {
        color: theme.palette.white.main,
        fontFamily: theme.typography.montserrat.bold,
        fontSize: "18px",
        lineHeight: "24px",
        letterSpacing: "-0.32px",
      },
      lastChance: {
        color: theme.palette.white.main,
        fontFamily: theme.typography.sourceSansPro.semibold,
        fontSize: isMobile ? "25px" : "27px",
        letterSpacing: "7.5px",
        lineHeight: "26px",
      },
    },
  },
});

const BookingDesktopNextMonth = (course) => {
  const { info, month, year, limitToApply } = course;

  const isMobile = useMediaQuery("(max-width:899px)");

  const theme = useTheme();
  const colors = useContext(ColorContext);
  const classes = useStyles(theme, isMobile, colors);
  const navigate = useNavigate();

  const { pathname } = useLocation();
  const componentName = "BookingDesktopNextMonth";
  const translation = useTranslation(pathname, componentName);
  const {
    typeText,
    inscriptionTimeLimitText,
    applyButtonContent,
    applyButtonContentNotAvailable,
    contents,
  } = translation;

  const contentToInfoMapper = bootcampContentToInfoMapper(course);

  const isAvailable =
    info?.availability && info?.vacancyStatus && info?.status === "disponible";

  return (
    <Box
      sx={{
        ...classes.apply,
        border: "solid 5px #fff",
        backgroundImage: "linear-gradient(to bottom, #fcfdfe 0%, #fcfdff 100%)",
        marginTop: "82px",
        background: "linear-gradient(to bottom, #fcfdfe 0%, #fcfdff 100%)",
        boxShadow: "0 4px 92px 0 rgba(0, 31, 85, 0.06)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          marginTop: "58px",
        }}
      >
        <Typography
          sx={{
            ...classes.typography.apply.start,
            color: theme.palette.blue.main,
          }}
        >
          <b>
            {month.toUpperCase()} {year} |
          </b>{" "}
          {typeText}
        </Typography>
        <Typography
          sx={{
            ...classes.typography.apply.end,
            color: theme.palette.blue.main,
          }}
        >
          {inscriptionTimeLimitText}
          <b>{formatDate(limitToApply)}</b>
        </Typography>
      </Box>
      <Divider
        variant="middle"
        sx={{
          width: "90%",
          height: "0.5px",
          margin: "15.5px 0px 36px",
          backgroundColor: theme.palette.white.main,
        }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-evenly",
          width: "100%",
        }}
      >
        <Box>
          {contents.slice(0, 3).map(({ title, img: { src, alt } }) => (
            <Box sx={classes.apply.listItem} key={title}>
              <img
                src={src}
                alt={alt}
                style={{
                  width: "21px",
                  height: "21px",
                  margin: "2.5px 7.6px 0 0",
                }}
              />
              <Box sx={classes.apply.listText}>
                <Typography
                  sx={{
                    ...classes.typography.apply.description,
                    color: theme.palette.blue.main,
                  }}
                >
                  {title}
                  <b> {contentToInfoMapper[title]}</b>
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box>
          {contents.slice(3, 6).map(({ title, img: { src, alt } }) => (
            <Box sx={classes.apply.listItem} key={title}>
              <img
                src={src}
                alt={alt}
                style={{
                  width: "21px",
                  height: "21px",
                  margin: "2.5px 7.6px 0 0",
                }}
              />
              <Box sx={classes.apply.listText}>
                <Typography
                  sx={{
                    ...classes.typography.apply.description,
                    color: theme.palette.blue.main,
                  }}
                >
                  {title}
                  <b> {contentToInfoMapper[title]}</b>
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ marginTop: "18px" }}>
          <Button
            onClick={() => {
              saveOnLS(info, () =>
                navigate(
                  `/aplica/${
                    course.tag === coursesTags.BC_JAVASCRIPT
                      ? "javascript"
                      : "salesforce"
                  }/formulario`
                )
              );
            }}
            disabled={!isAvailable}
            sx={{
              ...classes.apply.applyButton[
                isAvailable ? "enabled" : "disabled"
              ],
              background: "#f2f2f2",
              border: "1px solid lightgray",
            }}
            endIcon={
              <ArrowForwardIosIcon
                sx={{
                  ...classes.apply.icon[isAvailable ? "enabled" : "disabled"],
                  background: "none",
                  boxShadow: "none",
                  color: "rgba(0, 0, 0, 0.26)",
                }}
              />
            }
          >
            {info?.status !== "disponible"
              ? applyButtonContentNotAvailable
              : applyButtonContent}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BookingDesktopNextMonth;
